import { anachronies, displayOf, groupsOfType, mainTimelineOf, narrativeOrder, orderedGroups, type StoryNode } from '@dispositio/core';
import { de } from '../i18n/de';
import type { GuideEnv, GuideStep, GuideTarget } from './tutorialGuide';

/**
 * Führung „Rundgang“ an der „Schachnovelle“ (vollständige Fassung ohne eingebaute Fehler, apps/server/src/services/exampleSchach.ts): ein
 * Gang durch die Oberfläche – die Kopfleiste, dann die drei Modi von links nach rechts. Die Bibliothek mit Bereichen, Karten,
 * Kategorien, Pinnwand und der Seitenleiste einer Figur; die Handlung mit Fläche, Erzähllinie und ihrem Grundgedanken (Weltzeit gegen
 * Lesereihenfolge, die Rückblende), einer eigenen Szene, die der Nutzer anlegt und auf die Zeitlinie zieht, den Werkzeugen und der
 * Seitenleiste einer Szene; das Manuskript mit Binder, Seite, Werkzeugen, Element-Kontext, Kompass, Material, Versionen und Export.
 * Verknüpfen und Beziehungen zeigt das Tutorial „Verknüpfungen und Beziehungen“, Prüfen und die Übersichten die „Fehlersuche“.
 */

const T = de.guide.tour;
const css = (selector: string): GuideTarget => ({ selector });
const present = (selector: string): boolean => typeof document !== 'undefined' && !!document.querySelector(selector);

// bedienbare Bereiche der Schritte (siehe `allow`)
const VIEW_DESIGN = '[data-testid="view-design"]';
const VIEW_PLANNING = '[data-testid="view-planning"]';
const VIEW_EDITOR = '[data-testid="view-editor"]';
const CARDS = '[data-testid="design-cards"]';
const GRID = '[data-testid="design-grid"]';
const BOARD = '[data-testid="design-board"]';
const LIBRARY = '[data-testid="library-canvas"]';
const CANVAS = '[data-testid="graph-canvas"]';
const BAND = '[data-testid="narrative-band"]';
const ADD_SCENE = '[data-testid="line-add-scene"]';
/** eine Szene im Binder öffnen (nur ihr Titel: die Knöpfe der Zeile bleiben gesperrt) */
const BINDER_OPEN = 'aside.doc-tree [data-testid="scene-open"]';
/** die Seite des Manuskripts (nicht die Textfelder der Seitenleiste, die auch .rt-content sind) */
const PAGE = '.rt-content.rt-page';
const SIDE = 'aside.side';
const PANEL = `${SIDE} [role="tabpanel"]`;
/** die Knöpfe der Erzähllinie (Werkzeuge und Darstellung) */
const BAND_TOOLS = ['line-filter', 'line-status', 'line-previews', 'line-matrix', 'line-structure', 'line-film', 'line-knowledge', 'line-flow', 'line-all', 'line-compact', 'line-images', 'line-description', 'line-add-chapter']
  .map((id) => `[data-testid="${id}"]`)
  .join(', ');

/** die Szene mit diesem Titel (in Lesereihenfolge) */
const sceneNamed = (env: GuideEnv, title: string): StoryNode | undefined => narrativeOrder(env.ctx).scenes.find((s) => displayOf(env.ctx, s.scene).title === title)?.scene;
const entityOf = (env: GuideEnv, key: string) => env.ctx.story.entities.find((x) => x.refKey === key);
/** In der Seitenleiste ist diese Entität offen */
const entityOpen = (env: GuideEnv, key: string): boolean => env.ws.selection?.kind === 'entity' && env.ws.selection.id === entityOf(env, key)?.id;
/** Die Szene ist gewählt */
const sceneSelected = (env: GuideEnv, title: string): boolean => env.ws.selection?.kind === 'node' && env.ws.selection.id === sceneNamed(env, title)?.id;
/** Auswahl für `enter.select`: diese Entität bzw. Szene (zeigt die Seitenleiste auch nach dem Neuladen) */
const pickEntity = (key: string) => (env: GuideEnv) => {
  const id = entityOf(env, key)?.id;
  return id ? ({ kind: 'entity', id } as const) : null;
};
const pickScene = (title: string) => (env: GuideEnv) => {
  const id = sceneNamed(env, title)?.id;
  return id ? ({ kind: 'node', id } as const) : null;
};
/** ihre Karte in der Erzähllinie */
const lineCard = (scene: StoryNode | undefined): GuideTarget[] => (scene ? [css(`[data-testid="line-card"][data-scene-id="${scene.id}"]`)] : []);
/** ihre Marke bzw. ihr Balken auf der Fläche */
const onCanvas = (scene: StoryNode | undefined): GuideTarget[] => (scene ? [{ nodeId: scene.id, roles: ['event', 'period', 'card'] }] : []);
/** die Karte einer Entität in der Bibliothek */
const card = (env: GuideEnv, key: string): GuideTarget[] => [css(`[data-testid="entity-card"][data-entity-id="${entityOf(env, key)?.id ?? ''}"]`)];
/** der Kopf der ersten Kategorie der Personen */
const firstCategory = (env: GuideEnv): GuideTarget[] => {
  const person = env.ctx.story.types.find((t) => t.builtin && t.name === 'Person')?.id ?? '';
  const g = orderedGroups(groupsOfType(env.ctx, person))[0];
  return g ? [css(`[data-testid="category"][data-group-id="${g.id}"] [data-testid="category-head"]`), css(CARDS)] : [css(CARDS)];
};
/** Das Panel „Element-Kontext“ an einem verknüpften Wort ist offen */
const glanceOpen = (): boolean => present('[data-testid="ref-glance"]');

/** die Szene, die der Nutzer im Rundgang anlegt („+ Szene“ in der Erzähllinie; die Novelle hat keine mit diesem Titel) */
const ownScene = (env: GuideEnv): StoryNode | undefined =>
  [...narrativeOrder(env.ctx).scenes.map((s) => s.scene), ...narrativeOrder(env.ctx).unassigned].find((s) => displayOf(env.ctx, s).title === de.manuscript.sceneDefault);
/** Sie liegt an einer Zeitlinie */
const ownPlaced = (env: GuideEnv): boolean => {
  const scene = ownScene(env);
  return !!scene?.parentId && env.ctx.nodes.get(scene.parentId)?.kind === 'timeline';
};
/** „+ Szene“ im Kapitel der letzten Szene */
const lastChapterAdd = (env: GuideEnv): GuideTarget[] => {
  const chapter = sceneNamed(env, 'Remember')?.chapterId;
  return [...(chapter ? [css(`[data-testid="line-chapter"][data-node-id="${chapter}"] ${ADD_SCENE}`)] : []), css(BAND)];
};
/** die erste Rückblende des Buchs: Czentovics Kindheit („Bileams Esel“) */
const flashback = (env: GuideEnv): StoryNode | undefined => sceneNamed(env, 'Bileams Esel');

export const TOUR_STEPS: GuideStep[] = [
  // --- Kopfleiste ---
  { id: 'ru-start', text: () => T.start, targets: () => [css('[data-testid="viewbar"]')], blink: true },
  { id: 'ru-bar-left', text: () => T.barLeft, targets: () => [css('.topbar-start')] },
  { id: 'ru-bar-right', text: () => T.barRight, targets: () => [css('.topbar-end')] },

  // --- Bibliothek ---
  { id: 'ru-to-library', text: () => T.toLibrary, targets: () => [css(VIEW_DESIGN)], until: (e) => e.ws.view === 'design', allow: [VIEW_DESIGN] },
  { id: 'ru-areas', text: () => T.areas, targets: () => [css('[data-testid="design-rail"]')], enter: { designFocus: 'person' } },
  { id: 'ru-cards', text: () => T.cards, targets: (e) => [...card(e, 'drb'), css(CARDS)] },
  { id: 'ru-categories', text: () => T.categories, targets: firstCategory },
  { id: 'ru-toolbar', text: () => T.toolbar, targets: () => [css('[data-testid="design-toolbar"]')], also: () => [[css(GRID)]] },
  { id: 'ru-board', text: () => T.board, targets: () => [css(BOARD)], until: () => present(LIBRARY), allow: [BOARD] },
  { id: 'ru-board-view', text: () => T.boardView, targets: () => [css(LIBRARY)] },
  // Dr. B. öffnen: zurück zu den Karten (dort hat er einen Rahmen) – auf der Pinnwand geht es auch
  { id: 'ru-open-entity', text: () => T.openEntity, targets: (e) => [...card(e, 'drb'), css(GRID)], until: (e) => entityOpen(e, 'drb'), allow: [GRID, BOARD, CARDS, LIBRARY] },
  { id: 'ru-entity-head', text: () => T.entityHead, targets: () => [css(`${SIDE} .side-top`)], place: 'left', enter: { select: pickEntity('drb'), entityTab: 'info' } },
  { id: 'ru-entity-info', text: () => T.entityInfo, targets: () => [css(PANEL)], place: 'left', enter: { select: pickEntity('drb'), entityTab: 'info' } },
  { id: 'ru-entity-history', text: () => T.entityHistory, targets: () => [css(PANEL)], place: 'left', enter: { select: pickEntity('drb'), entityTab: 'history' } },
  { id: 'ru-entity-refs', text: () => T.entityRefs, targets: () => [css(PANEL)], place: 'left', enter: { select: pickEntity('drb'), entityTab: 'refs' } },
  { id: 'ru-import', text: () => T.import, targets: () => [css('[data-testid="library-import-open"]')] },

  // --- Handlung ---
  { id: 'ru-to-planning', text: () => T.toPlanning, targets: () => [css(VIEW_PLANNING)], until: (e) => e.ws.view === 'planning', allow: [VIEW_PLANNING] },
  { id: 'ru-canvas', text: () => T.canvas, targets: () => [css(CANVAS)], dock: 'canvas' },
  { id: 'ru-band', text: () => T.band, targets: () => [css(BAND)] },
  // der Grundgedanke: Weltzeit gegen Lesereihenfolge – eine Rückblende
  { id: 'ru-orders', text: () => T.orders, targets: (e) => lineCard(flashback(e)), also: (e) => [onCanvas(flashback(e))] },
  // eine eigene Szene: anlegen, auf die Zeitlinie ziehen
  { id: 'ru-add-scene', text: () => T.addScene, targets: lastChapterAdd, until: (e) => !!ownScene(e), allow: [ADD_SCENE] },
  {
    id: 'ru-place-scene',
    text: () => T.placeScene,
    targets: (e) => lineCard(ownScene(e)),
    also: (e) => {
      const main = mainTimelineOf(e.ctx);
      return main ? [[{ nodeId: main.id, roles: ['axis'] }]] : [];
    },
    until: ownPlaced,
    allow: [BAND, CANVAS],
  },
  {
    id: 'ru-placed',
    text: (e) => {
      const scene = ownScene(e);
      return T.placed(!!scene && anachronies(e.ctx).some((a) => a.sceneId === scene.id && a.kind === 'flashback'));
    },
    targets: (e) => onCanvas(ownScene(e)),
    also: (e) => [lineCard(ownScene(e))],
  },
  { id: 'ru-tools', text: () => T.tools, targets: () => [css('[data-testid="tool-palette"]')], also: () => [[css('[data-testid="add-node"]')]] },
  { id: 'ru-time', text: () => T.time, targets: () => [css('[data-testid="time-overview"]')] },
  { id: 'ru-band-tools', text: () => T.bandTools, targets: () => [css(BAND_TOOLS)] },
  { id: 'ru-select-scene', text: () => T.selectScene, targets: (e) => lineCard(sceneNamed(e, 'Remember')), until: (e) => sceneSelected(e, 'Remember'), allow: [BAND, CANVAS] },
  { id: 'ru-scene-info', text: () => T.sceneInfo, targets: () => [css(PANEL)], place: 'left', enter: { select: pickScene('Remember'), sceneTab: 'info' } },
  { id: 'ru-scene-time', text: () => T.sceneTime, targets: () => [css(PANEL)], place: 'left', enter: { select: pickScene('Remember'), sceneTab: 'time' } },
  { id: 'ru-scene-manuscript', text: () => T.sceneManuscript, targets: () => [css(PANEL)], place: 'left', enter: { select: pickScene('Remember'), sceneTab: 'manuscript' } },
  { id: 'ru-scene-objects', text: () => T.sceneObjects, targets: () => [css(PANEL)], place: 'left', enter: { select: pickScene('Remember'), sceneTab: 'objects' } },

  // --- Manuskript ---
  {
    id: 'ru-to-editor',
    text: () => T.toEditor,
    targets: (e) => [...lineCard(sceneNamed(e, 'Remember')), css(VIEW_EDITOR)],
    also: () => [[css(VIEW_EDITOR)]],
    until: (e) => e.ws.view === 'editor' && e.ws.openDoc !== null,
    allow: [BAND, CANVAS, VIEW_EDITOR, BINDER_OPEN],
  },
  { id: 'ru-binder', text: () => T.binder, targets: () => [css('aside.doc-tree')] },
  { id: 'ru-page', text: () => T.page, targets: () => [css(PAGE)] },
  { id: 'ru-format', text: () => T.format, targets: () => [css('.rt-toolbar')] },
  { id: 'ru-glance', text: () => T.glance, targets: () => [css(`${PAGE} > p:first-of-type .rt-ref-link[data-ref="czentovic"]`), css(`${PAGE} > p:first-of-type .rt-ref-link`), css(PAGE)], unlock: glanceOpen, dock: 'side', allow: [PAGE] },
  { id: 'ru-compass', text: () => T.compass, targets: () => [css('[data-testid="beat-compass"]'), css(PAGE)] },
  { id: 'ru-doc-head', text: () => T.docHead, targets: () => [css('.doc-head')] },
  { id: 'ru-export', text: () => T.export, targets: () => [css('[data-testid="export-open"]')] },

  // --- zum Schluss: was die anderen Tutorials zeigen ---
  { id: 'ru-end', text: () => T.end, targets: () => [css('[data-testid="brand-home"]')], also: () => [[css('[data-testid="open-issues"]')]], free: true },
];
